// Comparison engine: pair generation + content templates
// All firm data comes from API via firmStore
import { FIRM_BRANDING } from "./formatFirmDisplay";
import { getCachedFirm } from "./firmStore";
import { formatFirmPrice } from "./priceUtils";
import {
  getDrawdownLabel as _getDrawdownLabel,
  getDrawdownShort,
  getDrawdownDescription,
  formatConsistency,
  DRAWDOWN_LABELS,
  DRAWDOWN_LABELS_SHORT,
  normalizeDrawdownType,
} from "./firmRules";

// Adapt an API firm response: adds isExternal flag + safe defaults
export function adaptApiFirm(firm) {
  if (!firm) return null;
  return {
    ...firm,
    isExternal: firm.firm_type === "external",
    pricing: firm.pricing || [],
    profit_split: firm.profit_split || "N/A",
    drawdown_type: firm.drawdown_type || "",
    payout_frequency: firm.payout_frequency || "",
    first_payout_days: firm.first_payout_days || "",
    activation_fee: firm.activation_fee || "N/A",
    reset_fee: firm.reset_fee || "N/A",
    news_trading: firm.news_trading ?? false,
    algos_allowed: firm.algos_allowed ?? false,
    min_days: firm.min_days ?? 0,
    consistency_pct: firm.consistency_pct || null,
    consistency_rule: firm.consistency_rule || null,
    strengths: firm.strengths || null,
    weaknesses: firm.weaknesses || null,
    disclaimer: firm.disclaimer || "",
    affiliate_link: firm.affiliate_link || null,
  };
}

// Extract branding from API firm into camelCase shape
export function adaptBranding(firm) {
  const b = firm?.branding || {};
  return {
    brandColor: b.brand_color || "#3B82F6",
    textColor: b.text_color || "#FFFFFF",
    logo: b.logo || null,
    logoAlt: b.logo_alt || "",
    logoBgLight: b.logo_bg_light || false,
  };
}

// Get any firm by slug — from API cache
export function getComparisonFirm(slug) {
  return getCachedFirm(slug) || null;
}

// Get branding for any firm
export function getComparisonBranding(slug) {
  if (FIRM_BRANDING[slug]) return FIRM_BRANDING[slug];
  const cached = getCachedFirm(slug);
  if (cached?.branding) {
    return {
      brandColor: cached.branding.brand_color || cached.brand_color || "#3B82F6",
      textColor: cached.branding.text_color || "#FFFFFF",
      logo: cached.branding.logo,
      logoAlt: cached.branding.logo_alt,
    };
  }
  return { brandColor: "#3B82F6", textColor: "#FFFFFF" };
}

// Canonical ordering: alphabetical to prevent duplicates
export function canonicalPair(a, b) {
  return [a, b].sort().join("-vs-");
}

// Generate all comparison pairs for the initial rollout.
// ── Dedup guard ──────────────────────────────────────────────────────────
// Historical edits added a few pairs twice (once in one order, once the
// other, across different "Phase N" blocks). We normalize and de-duplicate
// at module init so buildCompareCards / getComparisonsForFirm never emit
// duplicate rows. Source-of-truth ordering preserved; duplicates are
// silently dropped.
const _RAW_COMPARISON_PAIRS = [
  // Internal firms vs Topstep
  { a: "bulenox", b: "topstep" },
  { a: "tradeday", b: "topstep" },
  { a: "blusky", b: "topstep" },
  { a: "elite-trader-funding", b: "topstep" },
  { a: "top-one-futures", b: "topstep" },
  { a: "e8-markets", b: "topstep" },
  { a: "blue-guardian", b: "topstep" },
  { a: "alpha-futures", b: "topstep" },
  // Internal firms vs Apex
  { a: "bulenox", b: "apex" },
  { a: "tradeday", b: "apex" },
  { a: "blusky", b: "apex" },
  { a: "elite-trader-funding", b: "apex" },
  { a: "top-one-futures", b: "apex" },
  { a: "e8-markets", b: "apex" },
  // High-value internal matchups
  { a: "bulenox", b: "elite-trader-funding" },
  { a: "blue-guardian", b: "tradeday" },
  { a: "bulenox", b: "e8-markets" },
  { a: "top-one-futures", b: "tradeday" },
  { a: "blusky", b: "tradeday" },
  { a: "bulenox", b: "tradeday" },
  // YRM Prop coverage
  { a: "yrm-prop", b: "topstep" },
  { a: "yrm-prop", b: "apex" },
  { a: "yrm-prop", b: "tradeday" },
  { a: "yrm-prop", b: "bulenox" },
  // AquaFutures coverage
  { a: "aquafutures", b: "topstep" },
  { a: "aquafutures", b: "apex" },
  { a: "aquafutures", b: "tradeday" },
  { a: "aquafutures", b: "elite-trader-funding" },
  // Alpha Futures expanded coverage
  { a: "alpha-futures", b: "apex" },
  { a: "alpha-futures", b: "tradeday" },
  { a: "alpha-futures", b: "bulenox" },
  // Blue Guardian expanded coverage
  { a: "blue-guardian", b: "apex" },
  { a: "blue-guardian", b: "bulenox" },
  // Cross-firm high-value matchups
  { a: "tradeday", b: "e8-markets" },
  { a: "bulenox", b: "blusky" },
  { a: "tradeday", b: "elite-trader-funding" },
  { a: "bulenox", b: "aquafutures" },
  { a: "bulenox", b: "top-one-futures" },
  // High-volume search query
  { a: "apex", b: "topstep" },
  // Phase 7 — targeted high-intent pairs
  { a: "blue-guardian", b: "elite-trader-funding" },
];

// Dedup guard retained as a defensive no-op: the historical "Phase 8 gap
// closure" block added 11 already-declared pairs; those were cleaned from
// source in a later pass. The canonical-key Set below guarantees any future
// accidental re-introduction still ships a dedup-clean export.
export const COMPARISON_PAIRS = (() => {
  const seen = new Set();
  const out = [];
  for (const pair of _RAW_COMPARISON_PAIRS) {
    const key = [pair.a, pair.b].sort().join("|");
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(pair);
  }
  return out;
})();

// Generate the canonical slug for a pair
export function pairSlug(a, b) {
  return canonicalPair(a, b);
}

// Check if a slug string matches any valid pair (in either order)
export function findPair(slug) {
  const parts = slug.split("-vs-");
  if (parts.length !== 2) return null;

  // Each "part" is a firm slug which may contain hyphens
  // Try all possible split points for the "-vs-" separator
  for (let i = 1; i < slug.length; i++) {
    const vsIdx = slug.indexOf("-vs-", i - 1);
    if (vsIdx === -1) break;
    const a = slug.substring(0, vsIdx);
    const b = slug.substring(vsIdx + 4);
    if (!a || !b) continue;

    const firmA = getComparisonFirm(a);
    const firmB = getComparisonFirm(b);
    if (firmA && firmB) {
      const canonical = canonicalPair(a, b);
      return { a, b, firmA, firmB, canonical, isCanonical: `${a}-vs-${b}` === canonical };
    }
  }
  return null;
}

// Helper: get 50K price — uses unified formatter
export function get50kPrice(firm) {
  const p = firm.pricing?.find(x => x.size_numeric === 50000)
    || firm.pricing?.find(x => x.size === "50K" || x.size === "$50K");
  if (!p) return "N/A";
  return formatFirmPrice(p, firm).display;
}

// Helper: generate strengths from firm data
export function getFirmStrengths(firm) {
  if (firm.strengths) return firm.strengths;
  const s = [];
  if (firm.first_payout_days?.toLowerCase().includes("day 1")) s.push("Day 1 payout processing");
  if (firm.activation_fee === "$0" || firm.activation_fee === "None") s.push("No activation fee");
  if (parseInt(firm.reset_fee?.replace(/[^0-9]/g, "")) <= 50) s.push(`Low reset fee (${firm.reset_fee})`);
  if (firm.news_trading) s.push("News trading allowed");
  if (firm.algos_allowed) s.push("Algos/EAs allowed");
  const split = firm.profit_split || "";
  if (split.includes("100%")) s.push(`${split} profit split`);
  else if (split) s.push(`${split} profit split`);
  if (firm.drawdown_type?.includes("Static") || firm.drawdown_type?.includes("trailing_intraday")) s.push("Multiple drawdown options");
  return s.length ? s : ["Competitive pricing", "Funded futures trading"];
}

// Helper: generate weaknesses from firm data
export function getFirmWeaknesses(firm) {
  if (firm.weaknesses) return firm.weaknesses;
  const w = [];
  if (firm.activation_fee && firm.activation_fee !== "$0" && firm.activation_fee !== "None") w.push(`${firm.activation_fee} activation fee`);
  if (!firm.news_trading) w.push("No news trading");
  if (!firm.algos_allowed) w.push("No algos/EAs");
  if (firm.consistency_pct) w.push(`${formatConsistency(firm)} consistency rule`);
  return w.length ? w : ["Newer firm — less track record"];
}

// Generate FAQ data for a comparison
export function generateFAQ(firmA, firmB) {
  const a = firmA.name;
  const b = firmB.name;
  const priceA = get50kPrice(firmA);
  const priceB = get50kPrice(firmB);
  return [
    { q: `Is ${a} better than ${b}?`, a: `It depends on your priorities. ${a} offers ${getDrawdownLabel(firmA)} drawdown at ${priceA} (50K). ${b} offers ${getDrawdownLabel(firmB)} drawdown at ${priceB} (50K). Compare payout speed, drawdown options, and total cost to decide.` },
    { q: `Which firm is cheaper — ${a} or ${b}?`, a: `${a}'s 50K evaluation costs ${priceA}. ${b}'s 50K evaluation costs ${priceB}. Factor in activation fees, reset fees, and subscription vs one-time pricing for the full picture.` },
    { q: `Which firm has faster payouts?`, a: `${a} pays out ${firmA.payout_frequency || "check website"}. ${b} pays out ${firmB.payout_frequency || "check website"}. First payout timelines differ — ${a}: ${firmA.first_payout_days || "varies"}, ${b}: ${firmB.first_payout_days || "varies"}.` },
    { q: `Can I trade news with ${a} or ${b}?`, a: `${a}: ${firmA.news_trading ? "Yes" : "No"}. ${b}: ${firmB.news_trading ? "Yes" : "No"}. Always verify current rules on each firm's website before trading news events.` },
    { q: `Which firm has better drawdown rules?`, a: `${a} uses ${getDrawdownLabel(firmA)} drawdown. ${b} uses ${getDrawdownLabel(firmB)} drawdown. ${firmA.drawdown_type?.includes("trailing_intraday") ? a + " offers multiple drawdown options at checkout." : ""}` },
    { q: `Which prop firm has easier evaluation rules — ${a} or ${b}?`, a: `${a} requires ${firmA.min_days ?? "N/A"} minimum eval days with ${formatConsistency(firmA)} consistency. ${b} requires ${firmB.min_days ?? "N/A"} minimum eval days with ${formatConsistency(firmB)} consistency. Fewer restrictions generally means an easier path to funding.` },
    { q: `Which prop firm has a better profit split?`, a: `${a} offers ${firmA.profit_split || "N/A"} profit split. ${b} offers ${firmB.profit_split || "N/A"} profit split. Some firms scale profit splits over time, so check each firm's current terms.` },
    { q: `Which is better for beginners — ${a} or ${b}?`, a: `Beginners should prioritize EOD drawdown, low minimum trading days, and no consistency rules. ${a} uses ${getDrawdownLabel(firmA)} drawdown with ${firmA.min_days ?? "N/A"} minimum days. ${b} uses ${getDrawdownLabel(firmB)} drawdown with ${firmB.min_days ?? "N/A"} minimum days. Choose the firm with simpler rules and a smaller account size to start.` },
  ];
}

// Re-export canonical helpers from firmRules.js (single source of truth)
export { formatConsistency, getDrawdownDescription, DRAWDOWN_LABELS };
export const getDrawdownLabel = _getDrawdownLabel;

// Generate comparison table rows
export function generateTableRows(firmA, firmB) {
  return [
    ["Eval Fee (50K)", get50kPrice(firmA), get50kPrice(firmB)],
    ["Drawdown Type", getDrawdownLabel(firmA), getDrawdownLabel(firmB)],
    ["Profit Split", firmA.profit_split || "N/A", firmB.profit_split || "N/A"],
    ["Min Eval Days", `${firmA.min_days ?? "N/A"} days`, `${firmB.min_days ?? "N/A"} days`],
    ["Payout Speed", firmA.payout_frequency || "N/A", firmB.payout_frequency || "N/A"],
    ["First Payout", firmA.first_payout_days || "N/A", firmB.first_payout_days || "N/A"],
    ["Activation Fee", firmA.activation_fee || "N/A", firmB.activation_fee || "N/A"],
    ["Reset Fee", firmA.reset_fee || "N/A", firmB.reset_fee || "N/A"],
    ["News Trading", firmA.news_trading ? "Yes" : "No", firmB.news_trading ? "Yes" : "No"],
    ["Algos/EAs", firmA.algos_allowed ? "Yes" : "No", firmB.algos_allowed ? "Yes" : "No"],
    ["Consistency Rule", formatConsistency(firmA), formatConsistency(firmB)],
  ];
}

// Get all valid comparison URLs for sitemap
export function getAllComparisonUrls() {
  return COMPARISON_PAIRS.map(({ a, b }) => `/compare/${canonicalPair(a, b)}`);
}

// ── Signal generation for comparison hub ────────────────────────────────

function shortDD(firm) {
  return getDrawdownShort(firm);
}

function shortConsistency(firm) {
  return formatConsistency(firm);
}

function shortPayout(firm) {
  const p = firm?.payout_frequency;
  if (!p) return "N/A";
  const l = p.toLowerCase();
  if (l.includes("day 1")) return "Day 1";
  if (l.includes("daily")) return "Daily";
  if (l.includes("weekly") && !l.includes("bi")) return "Weekly";
  if (l.includes("bi-weekly") || l.includes("biweekly")) return "Bi-weekly";
  if (l.includes("2x")) return "2x/mo";
  if (l.includes("8+")) return "8+ days";
  return p;
}

function numPrice(str) {
  if (!str) return 0;
  const m = str.match(/([\d,]+)/);
  return m ? parseInt(m[1].replace(",", "")) : 0;
}

// Generate max 2 compact data signals highlighting meaningful differences
export function generateSignals(firmA, firmB) {
  const signals = [];

  const pA = get50kPrice(firmA);
  const pB = get50kPrice(firmB);
  const nA = numPrice(pA);
  const nB = numPrice(pB);
  const lo = Math.min(nA, nB);
  const hi = Math.max(nA, nB);

  if (lo > 0 && hi > 0 && (hi - lo) / lo > 0.25) {
    signals.push(`${pA} vs ${pB}`);
  }

  if (signals.length < 2) {
    const dA = shortDD(firmA);
    const dB = shortDD(firmB);
    if (dA !== dB) signals.push(`${dA} vs ${dB} DD`);
  }

  if (signals.length < 2) {
    const cA = shortConsistency(firmA);
    const cB = shortConsistency(firmB);
    if (cA !== cB) signals.push(`${cA} vs ${cB} consistency`);
  }

  if (signals.length < 2) {
    const pyA = shortPayout(firmA);
    const pyB = shortPayout(firmB);
    if (pyA !== pyB) signals.push(`${pyA} vs ${pyB} payout`);
  }

  return signals;
}

// Get comparisons involving a specific firm (for internal linking)
export function getComparisonsForFirm(slug) {
  return COMPARISON_PAIRS
    .filter(({ a, b }) => a === slug || b === slug)
    .map(({ a, b }) => {
      const other = a === slug ? b : a;
      const otherFirm = getComparisonFirm(other);
      return {
        slug: canonicalPair(a, b),
        otherSlug: other,
        otherName: otherFirm?.name || other,
        url: `/compare/${canonicalPair(a, b)}`,
      };
    });
}
